import PlaceModel                from '@models/map/PlaceModel';
import { observer }              from 'mobx-react-lite';
import { DeleteOutlined }        from '@ant-design/icons';
import { EditOutlined }          from '@ant-design/icons';
import { HolderOutlined }        from '@ant-design/icons';
import { PlusOutlined }          from '@ant-design/icons';
import View                      from '@widesk-ui/components/View';
import FunctionalityGate         from '@/components/Functionality';
import useDesignTokens           from '@/hooks/useDesignTokens';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import fileStore                 from '@/stores/fileStore';
import FileImage                 from '@/components/FileImage';
import ListCollection            from '@widesk-ui/components/listCollection/ListCollection';
import PlaceTypeModel            from '@models/map/PlaceTypeModel';
import SelectPlaceType           from '@selects/map/SelectPlaceType';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import MultiplePlaceForm         from '@forms/map/MultiplePlaceForm';
import Button                    from '@widesk-ui/components/Button';
import { Modal }                 from '@widesk-ui/components/modal/Modal';
import { message }               from '@widesk-ui/hooks/useMessage';
import actionModel               from '@widesk-ui/tools/actionModel';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import ListContext               from '@widesk-ui/contexts/ListContext';
import React                     from 'react';
import startPlaceDrag            from '@/TokamapEditor/helpers/startPlaceDrag';
import ListEmptyState           from '@/TokamapEditor/ux/ListEmptyState';
import deleteShapesFromPlace     from '@/TokamapEditor/helpers/deleteShapesFromPlace';
import { functionalityStore }    from '@/components/Functionality';
import ZoneCountButton           from '@/TokamapEditor/ux/ZoneCountButton';
import { Shape }                 from '@tokamap/core';
import shapePlaceId              from '@/TokamapEditor/helpers/shapePlaceId';
import './PlaceListSidebar.scss';
import CompactControls from '@/TokamapEditor/ux/CompactControls';

const isShapeOfPlace = (shape: Shape, place: PlaceModel) => (
	shape.action === 'open_place'
	&& String((shape.entity as any)?.entity?.id ?? '') === String(place.id)
);

const shapesOfPlace = (place: PlaceModel) => currentEditorStore.allShapes.filter(shape => shapePlaceId(shape) === String(place.id));

/**
 * Enveloppe chaque ligne (voir `List.RowComponent`) pour permettre de glisser un point d'intérêt
 * vers une forme de la carte (voir `startPlaceDrag`) — en souris "manuelle", pas en glisser-déposer
 * HTML natif (voir le commentaire de `startPlaceDrag` sur pourquoi). `id` vient du `rowKey` de
 * `ListCollection` (`m => m.id?.toString()`) : on retrouve le modèle correspondant dans les données
 * déjà chargées de la liste (`ListContext`), pas besoin d'un aller-retour réseau.
 */
const DraggablePlaceRow = observer((props: { id: string; children: React.ReactNode }) => {
	const { listStore } = React.useContext(ListContext);
	const place = listStore?.data.find((m: PlaceModel) => m.id?.toString() === props.id) as PlaceModel | undefined;

	// Fichier déjà chargé par `itemProps.renderLeft` (voir plus bas) au moment où l'utilisateur peut
	// déclencher un drag : pas besoin de re-fetcher, juste de relire le résultat en cache. `observer`
	// est requis ici (le `RowComponent` de `List` n'est pas wrappé dans un `<Observer>` comme le sont
	// `renderLeft`/`renderRight`) pour que ce composant se re-rende une fois le fichier chargé.
	const file = place && fileStore.getFilesForModel(place, { fetchCollection: true }).at(0);
	const imageSrc = file?.public ? file.downloadPath : undefined;

	const hoverShapes = (hovered: boolean) => {
		const ids = hovered && place ? shapesOfPlace(place).map(shape => shape.id) : [];

		currentEditorStore.get?.mapAdapter.setHoveredShapes(ids);
	};

	React.useEffect(() => () => currentEditorStore.get?.mapAdapter.setHoveredShapes([]), []);

	return (
		<div
			className="place-draggable-row"
			style={{ cursor: place ? 'grab' : undefined }}
			onMouseDown={e => place && startPlaceDrag(place, e, imageSrc)}
			onMouseEnter={() => hoverShapes(true)}
			onMouseLeave={() => hoverShapes(false)}
		>
			{place && <HolderOutlined className="place-draggable-row_handle" />}
			{props.children}
		</div>
	);
});

const PlaceListSidebar = observer(() => {
	const projectEditor = currentProjectEditorStore.get;
	const project = projectEditor.project;
	const tokens = useDesignTokens();

	// Même structure que les autres listes : conteneur en pleine hauteur pour que le bouton tombe en
	// bas quand la liste est courte, `sticky` pour qu'il y reste quand elle déborde.

	return (
		<CompactControls>
			<View style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
				<View flex>
			<ListCollection
				emptyText={(
					<ListEmptyState
						title={`Aucun ${PlaceModel.localize('default', 'l')}`}
						hint={`Créez-en un, puis glissez-le sur une zone de la carte pour l'y relier.`}
					/>
				)}
				// La ligne entière ouvre le point d'intérêt, comme dans les listes de types et de thèmes.
				// Les cases à cocher restent cliquables pour la sélection multiple, et le glisser vers une
				// forme part d'un `mousedown` suivi d'un mouvement — il ne déclenche donc pas ce clic.
				onClickItem={m => {
					const first = shapesOfPlace(m as PlaceModel).at(0);

					if (!first) return leftSidebarStore.openPlace(m as PlaceModel);

					currentEditorStore.get.mapAdapter.setHoveredShapes([]);
					currentEditorStore.requestShapeSelection(first.id);
				}}
				onDidMount={listStore => {
					if (!leftSidebarStore.getListStore('places')) {
						leftSidebarStore.setListStore('places', listStore);
					}
				}}
				onFetchCollectionEnd={async coll => {
					const first = coll.first();
					if (first) await fileStore.fetchForModel(first, { fetchCollection: true });
				}}
				defaultSortName="label"
				collectionFilters={{
					placeType: {
						label: PlaceTypeModel.localize('plural'),
						filter: 'placeType',
						element: <SelectPlaceType allowClear projectUrn={project.urn} multiple />,
					},
				}}
				collectionSorts={{
					label: {
						labels: ['De A à Z', 'De Z à A'],
						value: 'label',
					},
					createdAt: {
						labels: ['Du plus récent au plus ancien', 'Du plus ancien au plus récent'],
						value: 'createdAt',
					},
				}}
				massActions={(selected, listStore) => {
					const checkedPlaceTypeUrns = selected.reduce<Urn[]>((acc, cur) => {
						const placeTypeUrn = cur.getUrn('placeType');
						if (!acc.includes(placeTypeUrn)) {
							acc.push(placeTypeUrn);
						}

						return acc;
					}, []);

					return [
						{
							disabled: checkedPlaceTypeUrns.length > 1,
							disabledMessage: `Vous ne pouvez pas modifier des ${PlaceModel.localize('plural', 'l')} avec différents types`,
							icon: <EditOutlined />,
							label: `Modifier la sélection (${selected.length})`,
							onClick: () => leftSidebarStore.lowerOpen({
								closeButton: true,
								render: () => <MultiplePlaceForm
									models={selected}
									onSuccess={() => {
										leftSidebarStore.closeLower();
									}}
								/>,
								parentTitle: `Points d'intérêts`,
								title: `Modifier la sélection (${selected.length})`,
								upperPush: true,
							}),
						},
						{
							danger: true,
							icon: <DeleteOutlined />,
							label: `Supprimer la sélection (${selected.length})`,
							onClick: async () => {
								const modelsLabel = PlaceModel.localize(selected.length > 1 ? 'plural' : 'default', 'l');
								const numberModelsLabel = `${selected.length} ${modelsLabel}`;
								return Modal.confirm({
									okText: 'Confirmer la suppression',
									okType: 'danger',
									title: `Supprimer ${numberModelsLabel} ?`,
									content: `Les zones associées à ce${selected.length > 1 ? 's' : ''} ${modelsLabel} seront aussi supprimées de la carte, 
										cette action est irréversible`,
									onOk: async () => {
										try {
											await Promise.all(selected.map(async place => {
												await deleteShapesFromPlace(place);
												await place.destroy();
											}));

											message.success(`La sélection de ${numberModelsLabel} a bien été supprimée.`);
											functionalityStore.refreshUsagesOf('place_limit');
											return listStore.reload();
										} catch (err) {
											message.exception(err);
										}
									},
								});
							},
						},
					];
				}}
				clickable
				manualActiveIndex
				RowComponent={DraggablePlaceRow}
				onFetchCollectionStart={async coll => {

					coll.setRequiredFilter('project', projectEditor.project.id);
				}}
				itemProps={(m, listStore) => {
					const shapes = currentEditorStore.allShapes.filter(shape => isShapeOfPlace(shape, m));

					return {
						actions: [
							actionModel.delete(m, {
								actionProps: { label: 'Supprimer' },
								content: shapes.length ?
									`Les zones associées à ce point d'intérêt (${shapes.length}) seront aussi supprimées de la carte, cette action est irréversible` :
									undefined,
								before: () => deleteShapesFromPlace(m),
								callback: () => {
									functionalityStore.refreshUsagesOf('place_limit');
									listStore.reload();
								},
							}),
						],
						renderRight: () => (
							<ZoneCountButton
								filter={shape => isShapeOfPlace(shape, m)}
								suffix={count => `associée${count > 1 ? 's' : ''}`}
							/>
						),
						renderLeft: () => (
							<FileImage
								file={fileStore.getFilesForModel(m, { fetchCollection: true }).at(0)}
								width={50}
								height={50}
								fit="cover"
								borderRadius={8}
								bordered
								preview={false}
							/>
						),
						subTitle: m.getUrn('placeType') ? m.placeType.renderName() : undefined,
						title: m.renderName(),
						extraProps: {
							paddingH: 'md',
							paddingV: 'sm',
						},
					};
				}}
				modelClass={PlaceModel}
			/>
				</View>

				{/* `fullWidth` sur le gate : son enveloppe est un `inline-flex` qui s'ajuste à son contenu, et
				    le bouton `block` cesserait de remplir la largeur dès la limite atteinte (voir `MapMenu`). */}
				<View
					p="sm"
					bg={tokens.surface.card}
					style={{ position: 'sticky', bottom: 0, borderTop: `1px solid ${tokens.border.subtle}` }}
				>
					<FunctionalityGate
						fullWidth
						reference="place_limit"
						context={currentProjectEditorStore.get.project}
					>
						<Button
							block
							size="large"
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => leftSidebarStore.openNewPlace()}
						>
							{`Ajouter ${PlaceModel.localize('a', 'l')}`}
						</Button>
					</FunctionalityGate>
				</View>
			</View>
		</CompactControls>
	);
});

// export

export default PlaceListSidebar;